import React, { useEffect, useState, useRef } from "react";
import style from "../css/ColorMatchGame.module.css";
import catch1 from "../../../icons/catch2.png";
import Confetti from "react-confetti";
import FanduEarnBanner from "./FanduEarnBanner";
import UAParser from "ua-parser-js";
import { ErrorHandling } from "../../../State";
import { offerEvent, updateFanduPoints } from "../../../utils/api";
import logo from "../../../icons/logoFanduW.svg";

const COLORS = [
  { name: "Red", color: "#EF4444" },
  { name: "Blue", color: "#3B82F6" },
  { name: "Green", color: "#22C55E" },
  { name: "Yellow", color: "#EAB308" },
  { name: "Purple", color: "#A855F7" },
  { name: "Orange", color: "#F97316" },
  { name: "Pink", color: "#EC4899" },
  { name: "Brown", color: "#92400E" },
];

export default function ColorMatchGame({
  autoStart = false,
  onClose,
  page = "Quiz_Categories",
  isContest,
  remark,
  time,
}) {
  const GAME_TIME = time;
  const [timeLeft, setTimeLeft] = useState(GAME_TIME);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);

  const [question, setQuestion] = useState(null);
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);

  let mId = localStorage.getItem("mId") || "2026";

  let uuid = localStorage.getItem("uuid");

  let session_id = localStorage.getItem("session_id");

  let userId = localStorage.getItem("userId");

  const offerRequestData = (eventMetaData) => {
    try {
      const parser = new UAParser();

      const result = parser.getResult();

      return {
        machine_id: mId || "",
        device_id: uuid || "",
        session_id: session_id || "",
        ip_address: localStorage.getItem("ip_address") || "",
        lat: "",
        lng: "",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        firstname: "",
        lastname: "",
        email: "",
        phone: "",
        deviceBrand: result?.device?.vendor || "Unknown",
        deviceModel: result?.device?.model || "Unknown",
        deviceType: result?.device?.type || "desktop",
        osName: result?.os?.name || "Unknown",
        osVersion: result?.os?.version || "Unknown",
        browserName: result?.browser?.name || "Unknown",
        browserVersion: result?.browser?.version || "Unknown",
        event_ts: Date.now().toString(),
        user_id: userId,
        ...eventMetaData,
      };
    } catch (error) {
      ErrorHandling("Unable to collect device information");

      return null;
    }
  };

  const avail = async (EventName, EventSubName, EventType, EventSubType) => {
    try {
      const requestData = offerRequestData({
        Event_Name: EventName,
        Event_Sub_Name: EventSubName,
        Event_Type: EventType,
        Event_Sub_Type: EventSubType,
      });

      if (!requestData) return;

      await offerEvent(requestData);
    } catch (error) {
      console.log("Offer Event Error:", error);
    }
  };

  const rewardColorGamePoints = async (correct, totalAttempts) => {
    try {
      const fanduUserId = localStorage.getItem("fanduUserId");

      if (!fanduUserId || totalAttempts <= 0) return;

      const wrong = totalAttempts - correct;

      // Correct answer = 2 points
      // Wrong answer = 1 point
      const totalPoints = correct * 2 + wrong;

      const rewardedCount =
        Number(localStorage.getItem("colorGameRewardCount")) || 0;

      // Only first 5 games are rewarded
      if (rewardedCount >= 5) {
        return;
      }

      const fanduPayload = {
        user_id: fanduUserId,
        score: totalPoints,
        is_contest: isContest ? 1 : 0,
        remark: remark ? "Color Game Contest" : "Color Game",
      };

      const response = await updateFanduPoints(fanduPayload);

      const updatedPoints = Number(response?.score) || 0;

      localStorage.setItem("userFanduPoints", updatedPoints);

      localStorage.setItem("colorGameRewardCount", rewardedCount + 1);

      if (!response?.success) {
        ErrorHandling(response.message);
      }
    } catch (error) {
      console.error("Color Game reward error:", error);
      ErrorHandling("Something went wrong. Try again later.");
    }
  };

  const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);

  const generateQuestion = () => {
    const shuffled = shuffle(COLORS);

    const answer = shuffled[0];

    const opts = shuffle([answer, shuffled[1], shuffled[2], shuffled[3]]);

    setQuestion(answer);
    setOptions(opts);
  };

  useEffect(() => {
    generateQuestion();
  }, []);

  const scoreRef = useRef(0);
  const totalAttemptsRef = useRef(0);

  useEffect(() => {
    scoreRef.current = score;
  }, [score]);

  useEffect(() => {
    if (!open || gameOver) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setGameOver(true);
          rewardColorGamePoints(scoreRef.current, totalAttemptsRef.current);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [open, gameOver]);

  const handleAnswer = (option) => {
    const isCorrect = option.name === question.name;

    totalAttemptsRef.current += 1;

    if (isCorrect) {
      setScore((prev) => prev + 1);
    }

    generateQuestion();
  };

  const startGame = () => {
    setOpen(true);
    setGameOver(false);
    setScore(0);
    setTimeLeft(GAME_TIME);
    totalAttemptsRef.current = 0;
    scoreRef.current = 0;
    generateQuestion();
    avail("Color_Game_Open", page, "Click", "Screen_Impressions");
  };

  useEffect(() => {
    if (autoStart) {
      startGame();
    }
  }, []);

  return (
    <>
      {!autoStart && (
        <QuizCard
          onClick={startGame}
          title="Color Match"
          image={catch1}
          bgColor="#a2b1f5ff"
          buttonColor="#270bf8ff"
        />
      )}
      {open && (
        <div className={style.overlay}>
          <div className={style.card}>
            <div style={{ backgroundColor: "#1143b8" }}>
              <img
                src={logo}
                style={{ width: "120px", padding: "10px 20px" }}
              />
            </div>
            {!gameOver ? (
              <>
                <div className={style.header}>
                  <div className={style.score}>Score: {score}</div>

                  <div className={style.timer}>{timeLeft}s</div>
                </div>

                <div
                  className={style.progress}
                  style={{
                    width: `${(timeLeft / time) * 100}%`,
                  }}
                />

                <div className={style.ruleBox}>🎯 Tap the matching color</div>

                <div className={style.gameArea}>
                  <p className={style.questionLabel}>Select the color below</p>

                  <div
                    className={style.colorQuestion}
                    style={{
                      color: question?.color,
                    }}
                  >
                    {question?.name}
                  </div>

                  <div className={style.optionGrid}>
                    {options.map((item) => (
                      <button
                        key={item.name}
                        className={style.optionButton}
                        style={{
                          background: item.color,
                        }}
                        onClick={() => handleAnswer(item)}
                      >
                        {/* {item.name} */}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <>
                <Confetti recycle={false} numberOfPieces={180} />

                <p className={style.resultTitle}>🎉 Time's Up</p>

                <div className={style.finalScore}>
                  {scoreRef.current * 2 +
                    (totalAttemptsRef.current - scoreRef.current)}{" "}
                  FP
                </div>

                <p className={style.finalText}>Fandu Points Earned</p>

                <div className={style.bestScore}>Your Score: {score}</div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <button
                    className={style.submitBtn}
                    onClick={() => {
                      if (autoStart) onClose();
                      else {
                        setOpen(false);
                        avail(
                          "Color_Game_Close",
                          "Quiz_Categories",
                          "Click",
                          "Screen_Impressions",
                        );
                      }
                    }}
                  >
                    Close
                  </button>

                  <button
                    className={style.submitBtn}
                    style={{
                      background:
                        "linear-gradient(to bottom, #FF9130 0%, #EC7000 100%)",
                    }}
                    onClick={() => {
                      startGame();

                      avail(
                        "Color_Game_Restart",
                        "Quiz_Categories",
                        "Click",
                        "Screen_Impressions",
                      );
                    }}
                  >
                    Play Again
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const QuizCard = ({ onClick, title, image, bgColor, buttonColor, avail }) => {
  return (
    <div
      className={style.quizCard}
      style={{
        background: `linear-gradient(to bottom, #FFFFFF, ${bgColor})`,
        border: `0.5px solid #EEEEEE`,
      }}
      onClick={() => onClick()}
    >
      <p className={style.quizTitle}>{title}</p>

      <div className={style.quizImageWrapper}>
        <img src={image} alt={title} className={style.quizImage} />
      </div>

      <div
        // type="button"
        className={style.quizButton}
        // style={{ backgroundColor: buttonColor }}
      >
        <FanduEarnBanner
          topText="Play &"
          bottomText="Earn"
          number={50}
          background={buttonColor}
          topTextColor="#FFFFFF"
          borderColor="#FAD140"
          bottomTextColor="#FAD140"
        />
      </div>
    </div>
  );
};
